import { enqueueSnackbar } from 'notistack';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import settings from '/@/core/settings';
import { _, getActiveLanguage } from '/@/locale';
import { axFetch } from '/@/store/session/actions';
import { invalidate } from '/@/utils/swr';
import type { UserNode } from '../nodes';

export type ActivityType = 'entrenamiento' | 'generacion_demanda';
export type ActivityModality = 'presencial' | 'virtual';
export type ActivityAttendance = 'si' | 'parcial' | 'no';

export interface ActivityParticipant {
    userNode: UserNode | null;
    otherName?: string;
    /** Name of the nearest Squad ancestor of userNode; null for Otros entries or nodes outside any squad. */
    squad?: string | null;
    attendance: ActivityAttendance;
    notes: string[];
}

export interface ActivityParticipantWrite {
    userNode?: number | null;
    otherName?: string;
    attendance: ActivityAttendance;
    notes?: string[];
}

export interface Activity {
    id: number;
    userNode: number;
    activityType: ActivityType;
    name: string;
    date: string;
    modality: ActivityModality;
    place?: string;
    topics: string[];
    budget?: string | null;
    note?: string;
    participants: ActivityParticipant[];
    speakers: UserNode[];
    otherSpeakers: string[];
    created: string;
    modified: string;
    createdBy?: number;
    updatedBy?: number;
}

export interface ActivityListItem {
    id: number;
    userNode: number;
    activityType: ActivityType;
    name: string;
    date: string;
    modality: ActivityModality;
    place?: string;
    budget?: string | null;
    participantsCount: number;
    attendanceSummary: Record<ActivityAttendance, number>;
    created: string;
    modified: string;
}

export interface ActivityWrite {
    activityType: ActivityType;
    name: string;
    date: string;
    modality: ActivityModality;
    place?: string;
    topics?: string[];
    budget?: number | string | null;
    note?: string;
    participants?: ActivityParticipantWrite[];
    speakers?: number[];
    otherSpeakers?: string[];
}

export interface PaginatedActivityList {
    count: number;
    next: string | null;
    previous: string | null;
    results: ActivityListItem[];
}

export interface ActivitiesListParams {
    page?: number;
    pageSize?: number;
    ordering?: string;
    activityType?: ActivityType;
    modality?: ActivityModality;
    name?: string;
    dateFrom?: string;
    dateTo?: string;
    /** Squad node ids (e.g. a Squad Coach's squads); when set, filters to those squads' activities. */
    squadNodeIds?: number[];
}

const getActivities = async (params: ActivitiesListParams & { key: string }) => {
    const searchParams = new URLSearchParams();

    if (params.page) searchParams.append('page', String(params.page));
    if (params.pageSize) searchParams.append('page_size', String(params.pageSize));
    if (params.ordering) searchParams.append('ordering', params.ordering);
    if (params.activityType) searchParams.append('activity_type', params.activityType);
    if (params.modality) searchParams.append('modality', params.modality);
    if (params.name) searchParams.append('name', params.name);
    if (params.dateFrom) searchParams.append('date_from', params.dateFrom);
    if (params.dateTo) searchParams.append('date_to', params.dateTo);
    if (params.squadNodeIds?.length) searchParams.append('squad_nodes', params.squadNodeIds.join(','));

    const apiUrl = new URL(
        `${settings.urls.apiDomain}/core/v1/activities/?${searchParams.toString()}`,
        window.location.origin
    );

    const res = await axFetch.get(apiUrl.toString());
    return res.data;
};

const getActivityById = async ({ id }: { id: number; key: string }) => {
    const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/activities/${id}/`, window.location.origin);

    const res = await axFetch.get(apiUrl.toString());
    return res.data;
};

const createActivity = async (data: ActivityWrite) => {
    const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/activities/`, window.location.origin);

    const res = await axFetch.post(apiUrl.toString(), data);
    return res.data;
};

const updateActivity = async ({ id, data }: { id: number; data: Partial<ActivityWrite> }) => {
    const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/activities/${id}/`, window.location.origin);

    const res = await axFetch.patch(apiUrl.toString(), data);
    return res.data;
};

const deleteActivity = async (id: number) => {
    const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/activities/${id}/`, window.location.origin);

    const res = await axFetch.delete(apiUrl.toString());
    return res.data;
};

export const useActivities = (
    params: ActivitiesListParams = {},
    options: { enabled?: boolean } = { enabled: true }
) => {
    const key = options.enabled ? { key: 'useActivities', ...params } : null;

    return useSWR<PaginatedActivityList>(key, getActivities, {
        revalidateIfStale: true,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        keepPreviousData: true,
    });
};

export const useActivityById = (id: number | undefined, options: { enabled?: boolean } = { enabled: true }) => {
    const key = options.enabled && id ? { key: 'useActivityById', id } : null;

    return useSWR<Activity>(key, getActivityById, {
        revalidateIfStale: true,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        keepPreviousData: true,
    });
};

export const useCreateActivity = () => {
    return useSWRMutation(
        { key: 'useCreateActivity' },
        (_key, { arg }: { arg: ActivityWrite }) => createActivity(arg),
        {
            onSuccess: () => {
                revalidateActivities();
            },
        }
    );
};

export const useUpdateActivity = (id: number) => {
    return useSWRMutation(
        { key: 'useUpdateActivity', id },
        (_key, { arg }: { arg: Partial<ActivityWrite> }) => updateActivity({ id, data: arg }),
        {
            onSuccess: () => {
                revalidateActivities(id);
            },
        }
    );
};

const getActivityPdfLink = async (id: number) => {
    const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/activities/${id}/pdf/link/`, window.location.origin);

    const res = await axFetch.post<{ url: string }>(apiUrl.toString(), { language: getActiveLanguage() });
    return res.data;
};

export const useDownloadActivityPdf = (activityId: number) => {
    return useSWRMutation({ key: 'useDownloadActivityPdf', activityId }, () => getActivityPdfLink(activityId));
};

/**
 * Downloads an activity's PDF: the caller shows its own loading state (button/icon spinner)
 * while this runs, and the tab opens only once the PDF is actually ready. Some browsers may
 * treat `window.open` this long after the click as an unrelated popup and block it — if so,
 * we surface a message asking the user to allow popups, rather than failing silently.
 */
export const useActivityPdfDownload = (activityId: number) => {
    const mutation = useDownloadActivityPdf(activityId);

    const download = async () => {
        try {
            const { url } = await mutation.trigger();
            const opened = window.open(url, '_blank');
            if (!opened) {
                enqueueSnackbar(
                    _(
                        'El navegador bloqueó la ventana. Habilitá las ventanas emergentes para este sitio e intentá de nuevo.'
                    ),
                    {
                        variant: 'warning',
                    }
                );
            }
        } catch {
            enqueueSnackbar(_('Error al generar el PDF'), { variant: 'error' });
        }
    };

    return { download, isDownloading: mutation.isMutating };
};

export const useDeleteActivity = ({ activityId }: { activityId: number }) => {
    return useSWRMutation({ key: 'useDeleteActivity', activityId }, () => deleteActivity(activityId), {
        onSuccess: () => {
            revalidateActivities(activityId);
            enqueueSnackbar(_('Actividad eliminada correctamente'), {
                variant: 'success',
            });
        },
    });
};

export const revalidateActivities = (id?: number) => {
    if (id) {
        invalidate({ key: 'useActivityById', id });
    }
    invalidate({ key: 'useActivities' });
};
